import Constants from '../../../app/utils/constants'
import arrowIcon from '../../../assets/images/icons/arrowicon.svg'
import { motion } from 'framer-motion'
import { useEffect, useRef, useState } from 'react'
import { toast } from 'react-toastify'

const OpenPositions = () => {
  const { heading, roles } = Constants.openPositions
  const isTwoCards = roles.length === 2
  const blobRef = useRef<HTMLDivElement>(null)
  const sectionRef = useRef<HTMLElement>(null)
  const [isInside, setIsInside] = useState(false)
  const [isModalOpen, setIsModalOpen] = useState(false)
  const [selectedRole, setSelectedRole] = useState<string>('')
  const [formData, setFormData] = useState({
    firstName: '',
    lastName: '',
    email: '',
    phone: '',
    experience: '',
    qualification: '',
    role: '',
    message: '',
    resume: null as File | null,
  })
  const [errors, setErrors] = useState<Record<string, string>>({})
  const [isSubmitting, setIsSubmitting] = useState(false)
  const [submitMessage, setSubmitMessage] = useState<{ type: 'success' | 'error'; text: string } | null>(null)

  const emailPattern = /^[^\s@]+@[^\s@]+\.[^\s@]+$/
  const phonePattern = /^\d{10}$/

  const validateAll = (data = formData) => {
    const nextErrors: Record<string, string> = {}
    if (!data.firstName.trim()) nextErrors.firstName = 'First name is required'
    if (!data.lastName.trim()) nextErrors.lastName = 'Last name is required'
    if (!data.email.trim()) nextErrors.email = 'Email is required'
    else if (!emailPattern.test(data.email.trim())) nextErrors.email = 'Email not valid'
    if (!data.phone.trim()) nextErrors.phone = 'Phone number is required'
    else if (!phonePattern.test(data.phone.trim())) nextErrors.phone = 'Phone number must be 10 digits'
    if (!data.experience.trim()) nextErrors.experience = 'Experience is required'
    if (!data.qualification.trim()) nextErrors.qualification = 'Qualification is required'
    if (!data.resume) nextErrors.resume = 'Resume is required'
    if (data.resume) {
      const allowedTypes = [
        'application/pdf',
        'application/msword',
        'application/vnd.openxmlformats-officedocument.wordprocessingml.document',
      ]
      if (!allowedTypes.includes(data.resume.type)) nextErrors.resume = 'Only PDF, DOC, DOCX are allowed'
      if (data.resume.size > 5 * 1024 * 1024) nextErrors.resume = 'Resume must be under 5MB'
    }
    return nextErrors
  }

  const openModal = (roleTitle: string) => {
    setSelectedRole(roleTitle)
    setFormData({
      firstName: '',
      lastName: '',
      email: '',
      phone: '',
      experience: '',
      qualification: '',
      role: roleTitle,
      message: '',
      resume: null,
    })
    setErrors({})
    setSubmitMessage(null)
    setIsModalOpen(true)
  }

  const uploadResumeToCloudinary = async (file: File) => {
    const cloudName = process.env.REACT_APP_CLOUDINARY_CLOUD_NAME || ''
    const uploadPreset = process.env.REACT_APP_CLOUDINARY_UPLOAD_PRESET || ''
    const folder = process.env.REACT_APP_CLOUDINARY_FOLDER || ''

    if (!cloudName || !uploadPreset) {
      return ''
    }

    const cloudinaryFormData = new FormData()
    cloudinaryFormData.append('file', file)
    cloudinaryFormData.append('upload_preset', uploadPreset)
    if (folder) cloudinaryFormData.append('folder', folder)

    const response = await fetch(`https://api.cloudinary.com/v1_1/${cloudName}/auto/upload`, {
      method: 'POST',
      body: cloudinaryFormData,
    })

    if (!response.ok) {
      return ''
    }

    const data = await response.json()
    return data.secure_url as string
  }

  const handleSubmit = async (event: React.FormEvent) => {
    event.preventDefault()
    setSubmitMessage(null)
    const nextErrors = validateAll()
    setErrors(nextErrors)

    if (Object.keys(nextErrors).length !== 0) return

    try {
      setIsSubmitting(true)

      const applyEndpoint = process.env.REACT_APP_APPLY_FORM_ENDPOINT || ''
      if (!applyEndpoint) {
        throw new Error('Apply endpoint is not configured')
      }

      const resumeUrl = formData.resume ? await uploadResumeToCloudinary(formData.resume) : ''

      const params = new URLSearchParams()
      const fullName = `${formData.firstName.trim()} ${formData.lastName.trim()}`.trim()
      params.append('firstName', formData.firstName.trim())
      params.append('lastName', formData.lastName.trim())
      // params.append('name', fullName)
      params.append('email', formData.email.trim())
      params.append('phone', formData.phone.trim())
      params.append('experience', formData.experience.trim())
      params.append('qualification', formData.qualification.trim())
      params.append('role', (formData.role || selectedRole).trim())
      params.append('message', formData.message.trim())
      params.append('resumeUrl', resumeUrl)

      await fetch(applyEndpoint, {
        method: 'POST',
        mode: 'no-cors',
        headers: {
          'Content-Type': 'application/x-www-form-urlencoded;charset=UTF-8',
        },
        body: params,
      })

      setSubmitMessage({ type: 'success', text: 'Application submitted successfully.' })
      toast.success('Application submitted successfully.')
      setFormData({
        firstName: '',
        lastName: '',
        email: '',
        phone: '',
        experience: '',
        qualification: '',
        role: '',
        message: '',
        resume: null,
      })
      setErrors({})

      setTimeout(() => {
        setIsModalOpen(false)
      }, 800)
    } catch (error) {
      const message = error instanceof Error ? error.message : 'Submission failed'
      setSubmitMessage({ type: 'error', text: message })
      toast.error(message)
    } finally {
      setIsSubmitting(false)
    }
  }

  useEffect(() => {
    if (!isModalOpen) return
    const scrollY = window.scrollY
    const previousBodyOverflow = document.body.style.overflow
    const previousHtmlOverflow = document.documentElement.style.overflow
    const previousBodyPosition = document.body.style.position
    const previousBodyTop = document.body.style.top
    const previousBodyLeft = document.body.style.left
    const previousBodyRight = document.body.style.right
    const previousBodyWidth = document.body.style.width

    document.body.style.overflow = 'hidden'
    document.documentElement.style.overflow = 'hidden'
    document.body.style.position = 'fixed'
    document.body.style.top = `-${scrollY}px`
    document.body.style.left = '0'
    document.body.style.right = '0'
    document.body.style.width = '100%'

    return () => {
      document.body.style.overflow = previousBodyOverflow
      document.documentElement.style.overflow = previousHtmlOverflow
      document.body.style.position = previousBodyPosition
      document.body.style.top = previousBodyTop
      document.body.style.left = previousBodyLeft
      document.body.style.right = previousBodyRight
      document.body.style.width = previousBodyWidth
      window.scrollTo(0, scrollY)
    }
  }, [isModalOpen])

  useEffect(() => {
    let mouseX = 0
    let mouseY = 0
    let currentX = 0
    let currentY = 0
    let rafId = 0

    const container = sectionRef.current
    if (!container) return

    const handleMouseMove = (e: MouseEvent) => {
      const rect = container.getBoundingClientRect()
      mouseX = e.clientX - rect.left
      mouseY = e.clientY - rect.top
    }

    const handleEnter = () => setIsInside(true)
    const handleLeave = () => setIsInside(false)

    const animate = () => {
      currentX += (mouseX - currentX) * 0.1
      currentY += (mouseY - currentY) * 0.1

      if (blobRef.current) {
        blobRef.current.style.transform = `translate(calc(${currentX}px - 50%), calc(${currentY}px - 50%))`
      }

      rafId = requestAnimationFrame(animate)
    }

    container.addEventListener('mousemove', handleMouseMove)
    container.addEventListener('mouseenter', handleEnter)
    container.addEventListener('mouseleave', handleLeave)

    rafId = requestAnimationFrame(animate)

    return () => {
      container.removeEventListener('mousemove', handleMouseMove)
      container.removeEventListener('mouseenter', handleEnter)
      container.removeEventListener('mouseleave', handleLeave)
      cancelAnimationFrame(rafId)
    }
  }, [])
  const cardEntryVariants = {
    hidden: (index: number) => {
      if (index === 1) {
        return { opacity: 0, y: 28, scale: 0.98 }
      }

      return {
        opacity: 0,
        y: 24,
        scale: 0.9,
        x: index === 0 ? 120 : -120,
      }
    },
    visible: {
      opacity: 1,
      y: 0,
      x: 0,
      scale: 1,
      transition: {
        duration: 1.20,
        ease: 'easeOut' as const,
      },
    },
  }

  return (
    <section ref={sectionRef} className="relative overflow-hidden bg-[#0E0924] px-4 md:pt-16 lg:pt-20">
      <div
        ref={blobRef}
        aria-hidden
        className={`pointer-events-none absolute z-0 h-72 w-72 rounded-full bg-[radial-gradient(circle_at_center,#9e5ddfba,transparent_70%)] blur-3xl transition-opacity duration-500 ease-out md:h-96 md:w-96 lg:h-125 lg:w-100 xl:h-140 xl:w-120 2xl:h-150 2xl:w-150 ${
          isInside ? 'opacity-60' : 'opacity-25'
        }`}
        style={{ transform: 'translate(85%, -35%)' }}
      />
      <div className="mx-auto flex max-w-4xl items-center justify-center gap-4 md:gap-6">
        <div className="h-px min-w-8 flex-1 bg-purple md:min-w-16" aria-hidden />
        <h2 className="shrink-0 text-center font-rajdhani text-3xl font-semibold text-white md:text-4xl lg:text-4xl xl:text-5xl 2xl:text-6xl">
          {heading}
        </h2>
        <div className="h-px min-w-8 flex-1 bg-purple md:min-w-16" aria-hidden />
      </div>

      <div
        className={`relative z-10 mx-auto mt-12 grid justify-items-center gap-6 pb-10 md:pb-14 lg:pb-20 ${
          isTwoCards
            ? 'md:grid-cols-2 md:gap-x-10 lg:gap-x-14'
            : 'md:grid-cols-3 lg:grid-cols-3'
        } ${
          isTwoCards
            ? 'md:max-w-[74%] lg:max-w-[68%] xl:max-w-[62%] 2xl:max-w-[58%]'
            : 'md:max-w-[99%] lg:max-w-[90%] xl:max-w-[80%] 2xl:max-w-[75%]'
        }`}
      >
        {roles.map((role, index) => (
          <motion.article
            key={role.title}
            className="group w-[95%] relative h-[250px] rounded-[30px] p-[1.5px] sm:h-[300px] md:h-[200px] lg:h-[220px] xl:h-[260px] 2xl:h-[320px]
            sm:w-[280px] md:w-[250px] lg:w-[280px] xl:w-[320px] 2xl:w-[410px] cursor-pointer
            bg-[linear-gradient(101.26deg,rgba(116,77,255,0.78)_0%,rgba(116,77,255,0.28)_50%,rgba(116,77,255,0.78)_100%)]"
            style={{ zIndex: index === 1 ? 20 : 10 }}
            variants={cardEntryVariants}
            custom={index}
            initial="hidden"
            whileInView="visible"
            viewport={{ once: false, amount: 0.35 }}
            onClick={() => openModal(role.title)}
          >
            {/* gradient overlay that fades in on hover to make the color change smooth */}
            <span
              aria-hidden
              className="absolute inset-0 rounded-[30px] bg-[linear-gradient(92.21deg,#EA892A_0%,#E35384_100%)] opacity-0 transition-opacity duration-[1150ms] ease-[cubic-bezier(0.22,1,0.36,1)] pointer-events-none group-hover:opacity-100"
            />
            <div className="relative z-10 space-y-2 md:space-y-2 flex h-full flex-col items-start justify-center overflow-hidden rounded-[30px] bg-[#0E0924] p-5 text-left md:p-6 lg:p-6 xl:p-7 2xl:p-8">
              <span className="pointer-events-none absolute -left-12 -top-12 h-[50%] w-[20%] rounded-full bg-purple/25 blur-2xl" />
              <span className="pointer-events-none absolute -bottom-12 -right-12 h-[50%] w-[20%] rounded-full bg-purple/10 blur-2xl" />
              <span className="absolute right-4 top-4 flex h-10 w-10 items-center justify-center rounded-full bg-purple transition-all duration-[900ms] ease-[cubic-bezier(0.22,1,0.36,1)] group-hover:scale-110 group-hover:bg-[linear-gradient(92.21deg,#EA892A_0%,#E35384_100%)] md:h-11 md:w-11 lg:h-12 lg:w-12">
                <img src={arrowIcon} alt="arrow icon" className="h-4 w-4 object-contain brightness-0 invert md:h-5 md:w-5 lg:h-6 lg:w-6 xl:h-6 xl:w-6 2xl:h-8 2xl:w-8" />
              </span>

              <h1 className="relative z-10 pr-14 font-gilroy-bold text-xl leading-tight text-white md:text-lg lg:text-xl xl:text-2xl 2xl:text-3xl">
                {role.title}
              </h1>
              <h3 className="relative z-10 lg:mt-3 font-gilroy-medium text-base text-white md:text-xs lg:text-sm xl:text-base 2xl:text-xl">
                {role.subtitle}
              </h3>
              <h6 className="relative z-10 lg:mt-3 2xl:mt-3 font-gilroy-medium text-base font-normal  tracking-wide text-gray-400 md:text-[11px] lg:text-xs 2xl:text-xl">
                {role.meta}
              </h6>
              <h6 className="relative z-10 lg:mt-1 font-gilroy-medium text-base font-normal tracking-wide text-gray-400 md:text-[11px] lg:text-xs 2xl:text-xl">
                {role.position}
              </h6>
              <h5 className="relative z-10 lg:mt-3 2xl:mt-4 bg-transparent font-gilroy-medium text-base text-white transition-all duration-[1000ms] ease-[cubic-bezier(0.22,1,0.36,1)] group-hover:bg-[linear-gradient(92.21deg,#EA892A_0%,#E35384_100%)] group-hover:bg-clip-text group-hover:text-transparent md:text-sm lg:text-sm xl:text-base 2xl:text-xl">
                {role.detail}
              </h5>
            </div>
          </motion.article>
        ))}
      </div>

      {isModalOpen && (
        <>
          <style>
            {`
              .career-apply-scroll {
                scrollbar-width: thin;
                scrollbar-color: #744DFF rgba(255,255,255,0.12);
              }
              .career-apply-scroll::-webkit-scrollbar {
                width: 10px;
              }
              .career-apply-scroll::-webkit-scrollbar-track {
                background: rgba(255,255,255,0.12);
                border-radius: 999px;
              }
              .career-apply-scroll::-webkit-scrollbar-thumb {
                background: #744DFF;
                border-radius: 999px;
              }
            `}
          </style>
          <div
            className="fixed inset-0 z-[1200] flex items-center justify-center bg-black/60 p-6"
            onWheelCapture={(event) => event.stopPropagation()}
            onTouchMoveCapture={(event) => event.stopPropagation()}
          >
          <div
            className="relative flex max-h-[90vh] w-full max-w-[980px] flex-col overflow-hidden rounded-[28px] border border-[#744DFF66] bg-[#130A31] p-6 md:p-8"
            data-lenis-prevent
            data-lenis-prevent-wheel
            data-lenis-prevent-touch
          >
            <button
              type="button"
              className="absolute right-6 top-5 text-[2rem] leading-none text-white/80 hover:text-white"
              onClick={() => setIsModalOpen(false)}
            >
              ×
            </button>

            <h2 className="font-rajdhani text-2xl md:text-3xl lg:text-[2.5rem] font-semibold text-white">Apply Now</h2>
            <div className="mt-3 h-px w-full bg-white/20" />

            <div className="career-apply-scroll mt-5 overflow-y-auto overscroll-contain pr-2" data-lenis-prevent>
            <form className="grid grid-cols-1 font-gilroy-medium gap-x-6 gap-y-5 md:grid-cols-2" noValidate onSubmit={handleSubmit}>
              <div>
                <label className="mb-2 block text-[1.15rem] text-white">First Name *</label>
                <input
                  className="h-14 w-full rounded-xl bg-white/10 px-4 text-white outline-none placeholder:text-white/45"
                  placeholder="First Name"
                  value={formData.firstName}
                  onChange={(e) => {
                    const v = e.target.value.replace(/[^A-Za-z\s]/g, '')
                    setFormData((p) => ({ ...p, firstName: v }))
                    setErrors((prev) => {
                      const next = { ...prev }
                      delete next.firstName
                      return next
                    })
                  }}
                />
                {errors.firstName && <p className="mt-1 text-xs text-red-300">{errors.firstName}</p>}
              </div>

              <div>
                <label className="mb-2 block  text-[1.15rem] text-white">Last Name *</label>
                <input
                  className="h-14 w-full rounded-xl bg-white/10 px-4  text-white outline-none placeholder:text-white/45"
                  placeholder="Last Name"
                  value={formData.lastName}
                  onChange={(e) => {
                    const v = e.target.value.replace(/[^A-Za-z\s]/g, '')
                    setFormData((p) => ({ ...p, lastName: v }))
                    setErrors((prev) => {
                      const next = { ...prev }
                      delete next.lastName
                      return next
                    })
                  }}
                />
                {errors.lastName && <p className="mt-1 text-xs text-red-300">{errors.lastName}</p>}
              </div>

              <div>
                <label className="mb-2 block  text-[1.15rem] text-white">Email ID *</label>
                <input
                  className="h-14 w-full rounded-xl bg-white/10 px-4  text-white outline-none placeholder:text-white/45"
                  placeholder="Email ID"
                  type="email"
                  value={formData.email}
                  onChange={(e) => {
                    const v = e.target.value
                    setFormData((p) => ({ ...p, email: v }))
                    setErrors((prev) => {
                      const next = { ...prev }
                      delete next.email
                      return next
                    })
                  }}
                />
                {errors.email && <p className="mt-1 text-xs text-red-300">{errors.email}</p>}
              </div>

              <div>
                <label className="mb-2 block  text-[1.15rem] text-white">Phone Number *</label>
                <input
                  className="h-14 w-full rounded-xl bg-white/10 px-4  text-white outline-none placeholder:text-white/45"
                  placeholder="Phone Number"
                  type="tel"
                  inputMode="numeric"
                  maxLength={10}
                  value={formData.phone}
                  onChange={(e) => {
                    const v = e.target.value.replace(/\D/g, '').slice(0, 10)
                    setFormData((p) => ({ ...p, phone: v }))
                    setErrors((prev) => {
                      const next = { ...prev }
                      delete next.phone
                      return next
                    })
                  }}
                />
                {errors.phone && <p className="mt-1 text-xs text-red-300">{errors.phone}</p>}
              </div>

              <div>
                <label className="mb-2 block  text-[1.15rem] text-white">Experience *</label>
                <input
                  className="h-14 w-full rounded-xl bg-white/10 px-4  text-white outline-none placeholder:text-white/45"
                  placeholder="Experience"
                  value={formData.experience}
                  onChange={(e) => {
                    const v = e.target.value
                    setFormData((p) => ({ ...p, experience: v }))
                    setErrors((prev) => {
                      const next = { ...prev }
                      delete next.experience
                      return next
                    })
                  }}
                />
                {errors.experience && <p className="mt-1 text-xs text-red-300">{errors.experience}</p>}
              </div>

              <div>
                <label className="mb-2 block  text-[1.15rem] text-white">Qualification *</label>
                <input
                  className="h-14 w-full rounded-xl bg-white/10 px-4  text-white outline-none placeholder:text-white/45"
                  placeholder="Qualification"
                  value={formData.qualification}
                  onChange={(e) => {
                    const v = e.target.value
                    setFormData((p) => ({ ...p, qualification: v }))
                    setErrors((prev) => {
                      const next = { ...prev }
                      delete next.qualification
                      return next
                    })
                  }}
                />
                {errors.qualification && <p className="mt-1 text-xs text-red-300">{errors.qualification}</p>}
              </div>

              <div className="md:col-span-2">
                <label className="mb-2 block  text-[1.15rem] text-white">Role</label>
                <input
                  className="h-14 w-full cursor-not-allowed rounded-xl bg-white/10 px-4  text-white/90 outline-none"
                  value={formData.role || selectedRole}
                  readOnly
                />
              </div>

              <div className="md:col-span-2">
                <label className="mb-2 block  text-[1.15rem] text-white">Message</label>
                <textarea
                  className="min-h-[120px] w-full rounded-xl bg-white/10 px-4 py-3  text-white outline-none placeholder:text-white/45"
                  placeholder="Type here..."
                  value={formData.message}
                  onChange={(e) => {
                    const v = e.target.value
                    setFormData((p) => ({ ...p, message: v }))
                    setErrors((prev) => {
                      const next = { ...prev }
                      delete next.message
                      return next
                    })
                  }}
                />
              </div>

              <div className="md:col-span-2">
                <label className="mb-2 block  text-[1.15rem] text-white">Upload Resume *</label>
                <label className="flex h-14 w-full cursor-pointer items-center justify-center rounded-xl border border-dashed border-white/60 bg-white/5  text-white/95">
                  {formData.resume ? formData.resume.name : 'Upload Resume *'}
                  <input
                    type="file"
                    className="hidden"
                    accept=".pdf,.doc,.docx"
                    onChange={(e) => {
                      const file = e.target.files?.[0] ?? null
                      setFormData((p) => ({ ...p, resume: file }))
                      setErrors((prev) => {
                        const next = { ...prev }
                        delete next.resume
                        return next
                      })
                    }}
                  />
                </label>
                {errors.resume && <p className="mt-1 text-xs text-red-300">{errors.resume}</p>}
              </div>

              <div className="md:col-span-2 pt-1">
                <button
                  type="submit"
                  disabled={isSubmitting}
                  className={`h-14 w-full rounded-xl font-gilroy-bold text-[1.35rem] transition-all duration-300 ${
                    !isSubmitting
                      ? 'bg-[#744DFF] text-white hover:brightness-110'
                      : 'bg-[#744DFF55] text-white/70 cursor-not-allowed'
                  }`}
                >
                  <span className="inline-flex items-center justify-center gap-3">
                    {isSubmitting && (
                      <span
                        aria-hidden
                        className="h-5 w-5 animate-spin rounded-full border-2 border-white/30 border-t-white"
                      />
                    )}
                    <span>{isSubmitting ? 'Submitting...' : 'Submit'}</span>
                  </span>
                </button>
              </div>
            </form>
            </div>
          </div>
        </div>
        </>
      )}
    </section>
  )
}

export default OpenPositions


